/**
 * PrintMailPacketTab.jsx
 *
 * User request (2026-02-28):
 *   "In the MAIL section I want it to be changed to mail and print… I want
 *    something to pop up where it allows you to select a previous client
 *    that we created a FTC report for and then select them, and they
 *    should be able to have all the preset documents which will be the
 *    cover letter, Sworn statement dispute letter, FTC report and ID if
 *    the ID isn't already there… mail per bureau just like inside of the
 *    flow of the FTC report builder."
 *
 * This component is the streamlined 4-step flow:
 *   1. Pick a client (dropdown of saved FTC reports + all clients)
 *   2. Presets auto-load (FTC, Sworn, Dispute, Client ID) as checkboxes
 *   3. Multi-select bureaus (Experian / Equifax / TransUnion)
 *   4. Click "Print [Bureau] Packet" → single PDF (bureau cover on top
 *      with the bureau's name, then the checked docs merged behind it)
 *
 * Backend contract (all under /api/print-mail):
 *   GET  /clients                         — dropdown data source
 *   GET  /preview/{ftc_report_id}         — checkbox availability
 *   POST /build-bureau-packet             — merger (returns PDF blob)
 *   POST /log                             — audit trail
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Printer, User, Search, FileText, CheckCircle2, AlertTriangle,
  Loader2, Upload, X, IdCard, Package, ClipboardList,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BUREAUS = [
  { key: 'experian',   label: 'Experian',   color: 'bg-emerald-700 hover:bg-emerald-800', border: 'border-emerald-500/40', tint: 'bg-emerald-500/10' },
  { key: 'equifax',    label: 'Equifax',    color: 'bg-red-700 hover:bg-red-800',         border: 'border-red-500/40',     tint: 'bg-red-500/10' },
  { key: 'transunion', label: 'TransUnion', color: 'bg-blue-700 hover:bg-blue-800',       border: 'border-blue-500/40',    tint: 'bg-blue-500/10' },
];

export default function PrintMailPacketTab() {
  // ─── Data / picker state ─────────────────────────────────────────
  const [clients, setClients]           = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [search, setSearch]             = useState('');
  const [selectedIdx, setSelectedIdx]   = useState(-1);
  const [preview, setPreview]           = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // ─── Doc checkboxes ─────────────────────────────────────────────
  const [includeFtc, setIncludeFtc]         = useState(true);
  const [includeSworn, setIncludeSworn]     = useState(true);
  const [includeDispute, setIncludeDispute] = useState(true);
  const [coverFile, setCoverFile]           = useState(null); // File (optional custom cover; falls back to auto-generated)
  const [coverDragOver, setCoverDragOver]   = useState(false);
  // "Use my exact FTC-flow PDFs" — verbatim overrides
  const [swornPdfFile, setSwornPdfFile]     = useState(null);
  const [disputePdfFile, setDisputePdfFile] = useState(null);
  const [ftcPdfFile, setFtcPdfFile]         = useState(null);
  const [idFile, setIdFile]                 = useState(null); // File
  const [extraFiles, setExtraFiles]         = useState([]);   // File[]
  const [idDragOver, setIdDragOver]         = useState(false);

  // ─── Bureau multi-select ─────────────────────────────────────────
  const [bureaus, setBureaus] = useState({ experian: true, equifax: true, transunion: true });

  // ─── Printing state ─────────────────────────────────────────────
  const [printing, setPrinting]  = useState({}); // {experian: bool, ...}

  // -----------------------------------------------------------------
  // Load clients list on mount
  // -----------------------------------------------------------------
  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const r = await axios.get(`${API}/api/print-mail/clients`);
      setClients(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  // -----------------------------------------------------------------
  // Filtered list (name / email search)
  // -----------------------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c => {
      const hay = `${c.name} ${c.email} ${c.phone} ${c.city}`.toLowerCase();
      return hay.includes(q);
    });
  }, [clients, search]);

  const selected = selectedIdx >= 0 ? filtered[selectedIdx] : null;

  // -----------------------------------------------------------------
  // Whenever a report-linked client is selected, hit preview endpoint
  // to determine which docs to check by default.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!selected?.ftc_report_id) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPreview(true);
      try {
        const r = await axios.get(`${API}/api/print-mail/preview/${selected.ftc_report_id}`);
        if (cancelled) return;
        setPreview(r.data || null);
        setIncludeFtc(!!r.data?.has_ftc);
        setIncludeSworn(!!r.data?.has_sworn);
        setIncludeDispute(!!r.data?.has_dispute);
      } catch (e) {
        if (!cancelled) toast.error('Failed to preview packet');
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.ftc_report_id]);

  // -----------------------------------------------------------------
  // Bulk Bureau Send — one PDF with ALL selected bureau packets stacked
  // (each with its own cover). Optional: email to admin.
  // Requested 2026-03-01: "Put it all in one PDF, but I don't want it
  // to be a zip file. I want it to be a PDF that can be saved to files
  // that could be uploaded to multiple different bureaus."
  // -----------------------------------------------------------------
  const [bulkBusy, setBulkBusy]             = useState(false);
  const [bulkEmailEnabled, setBulkEmailEnabled] = useState(false);
  const [bulkEmailTo, setBulkEmailTo]       = useState('clearallcreditrepair@gmail.com');

  const buildBulkPacket = async ({ emailIt }) => {
    if (!selected?.ftc_report_id) {
      toast.error('This client has no saved FTC report. Please create one in the FTC Builder first.');
      return;
    }
    const chosen = BUREAUS.filter(b => bureaus[b.key]).map(b => b.key);
    if (chosen.length === 0) { toast.error('Check at least one bureau'); return; }

    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append('ftc_report_id', selected.ftc_report_id);
      fd.append('bureaus', chosen.join(','));
      fd.append('include_ftc',     includeFtc ? 'true' : 'false');
      fd.append('include_sworn',   includeSworn ? 'true' : 'false');
      fd.append('include_dispute', includeDispute ? 'true' : 'false');
      fd.append('email', emailIt ? 'true' : 'false');
      if (emailIt) fd.append('email_to', (bulkEmailTo || '').trim() || 'clearallcreditrepair@gmail.com');
      if (coverFile) fd.append('cover_document', coverFile, coverFile.name);
      if (swornPdfFile) fd.append('sworn_pdf', swornPdfFile, swornPdfFile.name);
      if (disputePdfFile) fd.append('dispute_pdf', disputePdfFile, disputePdfFile.name);
      if (ftcPdfFile) fd.append('ftc_pdf', ftcPdfFile, ftcPdfFile.name);
      if (idFile) fd.append('id_document', idFile, idFile.name);
      (extraFiles || []).forEach(f => fd.append('additional_docs', f, f.name));

      const r = await axios.post(`${API}/api/print-mail/build-combined-packet`, fd, {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);

      // Trigger download so the user gets one save-to-files action
      const a = document.createElement('a');
      a.href = url;
      const safeName = (selected.name || 'client').replace(/[^A-Za-z0-9]+/g, '_');
      a.download = `${safeName}_BULK_${chosen.join('-').toUpperCase()}_packet.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      const emailStatus = r.headers?.['x-email-status'] || r.headers?.get?.('x-email-status');
      if (emailIt) {
        if (emailStatus === 'sent') {
          toast.success(`Bulk packet downloaded & emailed to ${bulkEmailTo}`);
        } else {
          const err = r.headers?.['x-email-error'] || r.headers?.get?.('x-email-error') || 'Email delivery failed';
          toast.error(`Packet downloaded but email failed: ${err}`);
        }
      } else {
        toast.success(`Bulk packet ready (${chosen.length} bureau${chosen.length > 1 ? 's' : ''} in one PDF)`);
      }

      // Audit log — fire-and-forget
      try {
        const docCount = ((includeFtc ? 1 : 0) + (includeSworn ? 1 : 0) + (includeDispute ? 1 : 0)
                       + (idFile ? 1 : 0) + (extraFiles || []).length + 1) * chosen.length;
        await axios.post(`${API}/api/print-mail/log`, {
          ftc_report_id: selected.ftc_report_id,
          client_id: selected.client_id || null,
          client_name: selected.name,
          bureaus: chosen,
          doc_count: docCount,
          note: emailIt ? `bulk-combined + emailed to ${bulkEmailTo}` : 'bulk-combined download',
        });
      } catch(_e) {/* silent */}

      // Free the blob URL after a moment
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to build bulk packet');
    } finally {
      setBulkBusy(false);
    }
  };

  // -----------------------------------------------------------------
  // Print a single bureau packet
  // -----------------------------------------------------------------
  const printBureau = async (bKey) => {
    if (!selected?.ftc_report_id) {
      toast.error('This client has no saved FTC report. Please create one in the FTC Builder first, or drop your own docs in the extras zone below.');
      return;
    }
    setPrinting(p => ({ ...p, [bKey]: true }));
    try {
      const fd = new FormData();
      fd.append('ftc_report_id', selected.ftc_report_id);
      fd.append('bureau', bKey);
      fd.append('include_ftc',     includeFtc ? 'true' : 'false');
      fd.append('include_sworn',   includeSworn ? 'true' : 'false');
      fd.append('include_dispute', includeDispute ? 'true' : 'false');
      if (coverFile) fd.append('cover_document', coverFile, coverFile.name);
      if (swornPdfFile) fd.append('sworn_pdf', swornPdfFile, swornPdfFile.name);
      if (disputePdfFile) fd.append('dispute_pdf', disputePdfFile, disputePdfFile.name);
      if (ftcPdfFile) fd.append('ftc_pdf', ftcPdfFile, ftcPdfFile.name);
      if (idFile) fd.append('id_document', idFile, idFile.name);
      (extraFiles || []).forEach(f => fd.append('additional_docs', f, f.name));

      const r = await axios.post(`${API}/api/print-mail/build-bureau-packet`, fd, {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const w    = window.open(url, '_blank');
      if (w) { w.focus(); setTimeout(() => { try { w.print(); } catch(_e){/* silent */} }, 800); }
      toast.success(`${BUREAUS.find(b => b.key === bKey)?.label} packet ready to print`);

      // Audit log — fire-and-forget
      try {
        const docCount = (includeFtc ? 1 : 0) + (includeSworn ? 1 : 0) + (includeDispute ? 1 : 0)
                       + (idFile ? 1 : 0) + (extraFiles || []).length + 1; // +1 for cover
        await axios.post(`${API}/api/print-mail/log`, {
          ftc_report_id: selected.ftc_report_id,
          client_id: selected.client_id || null,
          client_name: selected.name,
          bureaus: [bKey],
          doc_count: docCount,
        });
      } catch(_e) {/* silent */}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to build bureau packet');
    } finally {
      setPrinting(p => ({ ...p, [bKey]: false }));
    }
  };

  // Print all checked bureaus in one go (sequential — user has 3 tabs
  // open at end, each auto-printing).
  const printAllChecked = async () => {
    const chosen = BUREAUS.filter(b => bureaus[b.key]).map(b => b.key);
    if (chosen.length === 0) { toast.error('Check at least one bureau'); return; }
    for (const k of chosen) {
      await printBureau(k);
      await new Promise(res => setTimeout(res, 400));
    }
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <div className="space-y-4" data-testid="print-mail-packet-tab">
      {/* ── Step 1: Client picker ────────────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">1</span>
            Pick a client
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelectedIdx(-1); }}
              placeholder="Search by name, email, phone, city…"
              className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              data-testid="print-mail-search-input"
            />
          </div>

          {loadingClients && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
            </div>
          )}

          {!loadingClients && (
            <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
              {filtered.length === 0 && (
                <div className="text-zinc-500 text-sm py-6 text-center">
                  No clients match &quot;{search}&quot;
                </div>
              )}
              {filtered.map((c, i) => {
                const isSel = i === selectedIdx;
                const hasReport = !!c.ftc_report_id;
                return (
                  <button
                    key={`${c.client_id || c.ftc_report_id || i}`}
                    onClick={() => setSelectedIdx(i)}
                    data-testid={`print-mail-client-${i}`}
                    className={`w-full text-left px-3 py-2 rounded-md border transition-colors flex items-center justify-between gap-3 ${
                      isSel
                        ? 'border-purple-500 bg-purple-500/20'
                        : hasReport
                          ? 'border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10'
                          : 'border-zinc-700 bg-zinc-800/40 hover:bg-zinc-800/70'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <User className={`w-4 h-4 flex-shrink-0 ${isSel ? 'text-purple-300' : hasReport ? 'text-emerald-400' : 'text-zinc-400'}`} />
                      <div className="min-w-0">
                        <div className={`text-sm font-semibold truncate ${isSel ? 'text-white' : 'text-zinc-100'}`}>
                          {c.name || '(no name)'}
                          {c.is_orphan && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">Orphan Report</span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-400 truncate">
                          {c.email || '—'} {c.phone ? `· ${c.phone}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {hasReport ? (
                        <div className="flex flex-col items-end">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            {c.account_count} account{c.account_count === 1 ? '' : 's'}
                          </span>
                          {c.saved_letter_count > 0 && (
                            <span className="text-[10px] text-zinc-400 mt-0.5">
                              {c.saved_letter_count} letter{c.saved_letter_count === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-zinc-500">No report</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Step 2: Presets ───────────────────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">2</span>
            Documents to include
          </CardTitle>
          {selected && (
            <p className="text-[12px] text-zinc-400">
              {selected.name} · {selected.ftc_report_id
                ? `Report #${selected.report_number || (selected.ftc_report_id || '').slice(0, 6)}`
                : 'no saved FTC report — drag your own docs into the extras zone below'}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!selected && (
            <div className="text-zinc-500 text-sm">Pick a client first ↑</div>
          )}

          {selected && loadingPreview && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading saved report…
            </div>
          )}

          {selected && !loadingPreview && (
            <>
              {/* Preset doc rows (each is a checkbox) */}
              <PresetRow
                checked={includeFtc}
                onChange={setIncludeFtc}
                disabled={!preview?.has_ftc}
                label="FTC Identity Theft Report"
                sub={preview?.has_ftc ? `${preview?.account_count || 0} fraudulent account(s) will be regenerated fresh from the saved report` : 'No accounts on saved report — cannot include'}
                testid="preset-ftc"
              />
              <PresetRow
                checked={includeSworn}
                onChange={setIncludeSworn}
                disabled={!preview?.has_sworn}
                label="Sworn Statement / Identity Theft Affidavit"
                sub="Regenerated using the client's saved information"
                testid="preset-sworn"
              />
              <PresetRow
                checked={includeDispute}
                onChange={setIncludeDispute}
                disabled={!preview?.has_dispute}
                label="Dispute Letter"
                sub={preview?.has_dispute
                  ? `Using saved letter${preview?.latest_letter_bureau ? ` (originally for ${preview.latest_letter_bureau})` : ''} — the cover page's bureau will be rewritten to match each bureau you print`
                  : 'No saved dispute letter for this client — go to the FTC Builder and generate one to include it here'}
                testid="preset-dispute"
              />

              {/* Cover letter (auto-generated OR upload custom) */}
              <div
                className={`rounded-lg border p-3 ${coverFile ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-zinc-700 bg-zinc-800/40'}`}
                data-testid="preset-cover-row"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FileText className={`w-5 h-5 ${coverFile ? 'text-emerald-400' : 'text-zinc-400'}`} />
                    <div>
                      <div className="text-sm font-semibold text-white">
                        Cover Letter{' '}
                        {!coverFile && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                            Auto-generated
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-400">
                        {coverFile
                          ? coverFile.name
                          : 'First page of the packet — ATTN: FRAUD DISPUTE DEPARTMENT, then Name / DOB / SSN / Address / Phone / Email. Drop a PDF or image to use your own cover instead.'}
                      </div>
                    </div>
                  </div>
                  {coverFile && (
                    <Button variant="ghost" size="sm" onClick={() => setCoverFile(null)} data-testid="preset-cover-clear">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div
                  onDragOver={(e) => { e.preventDefault(); setCoverDragOver(true); }}
                  onDragLeave={() => setCoverDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setCoverDragOver(false);
                    const dropped = Array.from(e.dataTransfer.files || []);
                    if (!dropped.length) return;
                    setCoverFile(dropped[0]);
                  }}
                  className={`mt-2 border-2 border-dashed rounded-md p-3 text-center text-[12px] transition-colors ${
                    coverDragOver ? 'border-blue-400 bg-blue-500/10' : 'border-zinc-600'
                  }`}
                >
                  <Upload className="w-4 h-4 mx-auto mb-1 text-zinc-500" />
                  <label className="text-blue-400 underline cursor-pointer">
                    Drag a cover PDF/image here or pick from device
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setCoverFile(f); e.target.value = ''; }}
                      data-testid="preset-cover-input"
                    />
                  </label>
                </div>
              </div>

              {/* Verbatim FTC-flow overrides — drop the exact PDFs the user
                  already downloaded from the FTC Builder to guarantee the
                  packet's docs are BYTE-IDENTICAL to what they saw before. */}
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="verbatim-overrides-row">
                <div className="flex items-start gap-2">
                  <FileText className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-white">
                      Use my exact FTC-flow PDFs (recommended)
                    </div>
                    <div className="text-[11px] text-zinc-400">
                      Drop the exact FTC Report / Sworn Statement / Dispute Letter PDFs you already downloaded from the FTC Builder. When present, the packet uses them BYTE-FOR-BYTE — no regeneration, no LLM re-drafting.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <VerbatimSlot label="FTC Report PDF" file={ftcPdfFile} setFile={setFtcPdfFile} testid="verbatim-ftc" />
                  <VerbatimSlot label="Sworn Statement PDF" file={swornPdfFile} setFile={setSwornPdfFile} testid="verbatim-sworn" />
                  <VerbatimSlot label="Dispute Letter PDF" file={disputePdfFile} setFile={setDisputePdfFile} testid="verbatim-dispute" />
                </div>
              </div>

              {/* Client ID (auto-loaded from client record OR upload) */}
              <div
                className={`rounded-lg border p-3 ${idFile ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-zinc-700 bg-zinc-800/40'}`}
                data-testid="preset-id-row"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <IdCard className={`w-5 h-5 ${idFile ? 'text-emerald-400' : 'text-zinc-400'}`} />
                    <div>
                      <div className="text-sm font-semibold text-white">Government-issued ID {selected.has_saved_id && !idFile && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Auto-loaded from client profile</span>}</div>
                      <div className="text-[11px] text-zinc-400">
                        {idFile
                          ? idFile.name
                          : (selected.has_saved_id
                              ? 'Will attach automatically. Drop a new file to override.'
                              : 'No ID on file. Drag one here — copy of driver&apos;s license or passport recommended.')}
                      </div>
                    </div>
                  </div>
                  {idFile && (
                    <Button variant="ghost" size="sm" onClick={() => setIdFile(null)} data-testid="preset-id-clear">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div
                  onDragOver={(e) => { e.preventDefault(); setIdDragOver(true); }}
                  onDragLeave={() => setIdDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIdDragOver(false);
                    const dropped = Array.from(e.dataTransfer.files || []);
                    if (!dropped.length) return;
                    setIdFile(dropped[0]);
                  }}
                  className={`mt-2 border-2 border-dashed rounded-md p-3 text-center text-[12px] transition-colors ${
                    idDragOver ? 'border-blue-400 bg-blue-500/10' : 'border-zinc-600'
                  }`}
                >
                  <Upload className="w-4 h-4 mx-auto mb-1 text-zinc-500" />
                  <label className="text-blue-400 underline cursor-pointer">
                    Drag ID file here or pick from device
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setIdFile(f); e.target.value = ''; }}
                      data-testid="preset-id-input"
                    />
                  </label>
                </div>
              </div>

              {/* Extra files (letters etc) */}
              <ExtraDocsZone files={extraFiles} setFiles={setExtraFiles} />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Step 3: Bureau multi-select ───────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">3</span>
            Which bureaus?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {BUREAUS.map(b => (
              <label
                key={b.key}
                className={`flex items-center gap-2 p-3 rounded-md border cursor-pointer transition-colors ${
                  bureaus[b.key]
                    ? `${b.border} ${b.tint}`
                    : 'border-zinc-700 bg-zinc-800/40 hover:bg-zinc-800/70'
                }`}
                data-testid={`print-mail-bureau-${b.key}`}
              >
                <input
                  type="checkbox"
                  checked={!!bureaus[b.key]}
                  onChange={(e) => setBureaus(prev => ({ ...prev, [b.key]: e.target.checked }))}
                  className="w-4 h-4 accent-purple-500"
                />
                <span className="text-white font-semibold">{b.label}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Step 4: Print action ─────────────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">4</span>
            Print packets
          </CardTitle>
          <p className="text-[12px] text-zinc-400">
            Each bureau&apos;s PDF opens in a new tab with its own cover page (bureau name in bold red at the top), then the checked docs merged behind it. Print each one, staple, and drop at USPS.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {!selected && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-950/40 text-amber-200 px-3 py-2 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              Pick a client above to enable printing.
            </div>
          )}

          {selected && (
            <>
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                onClick={printAllChecked}
                disabled={Object.values(bureaus).every(v => !v) || Object.values(printing).some(v => v)}
                data-testid="print-mail-print-all"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print all checked bureaus ({Object.values(bureaus).filter(v => v).length})
              </Button>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {BUREAUS.map(b => (
                  <Button
                    key={b.key}
                    onClick={() => printBureau(b.key)}
                    disabled={!bureaus[b.key] || printing[b.key]}
                    className={`${b.color} text-white`}
                    data-testid={`print-mail-print-${b.key}`}
                  >
                    {printing[b.key] ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</>
                    ) : (
                      <>🖨️ Print {b.label} Packet</>
                    )}
                  </Button>
                ))}
              </div>

              {/* ── Bulk Bureau Send — single-PDF combined packet ── */}
              <div
                className="mt-4 rounded-lg border border-fuchsia-500/40 bg-fuchsia-950/30 p-3 space-y-3"
                data-testid="bulk-bureau-send-section"
              >
                <div className="flex items-start gap-2">
                  <Package className="w-5 h-5 text-fuchsia-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-white font-semibold text-sm">
                      Bulk Bureau Send — one PDF for all checked bureaus
                    </div>
                    <p className="text-[11px] text-fuchsia-200/80 mt-0.5">
                      Combines every checked bureau&apos;s cover + docs into a
                      SINGLE downloadable PDF (not a zip). Save to Files, then
                      upload to each bureau&apos;s online portal — or email a
                      copy to the admin below.
                    </p>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-[12px] text-fuchsia-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkEmailEnabled}
                    onChange={(e) => setBulkEmailEnabled(e.target.checked)}
                    className="w-4 h-4 accent-fuchsia-500"
                    data-testid="bulk-email-enable"
                  />
                  Also email this packet as an attachment
                </label>
                {bulkEmailEnabled && (
                  <Input
                    type="email"
                    value={bulkEmailTo}
                    onChange={(e) => setBulkEmailTo(e.target.value)}
                    placeholder="recipient@email.com"
                    className="bg-zinc-900 border-fuchsia-500/30 text-white text-sm"
                    data-testid="bulk-email-to"
                  />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    onClick={() => buildBulkPacket({ emailIt: false })}
                    disabled={bulkBusy || Object.values(bureaus).every(v => !v)}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    data-testid="bulk-download-btn"
                  >
                    {bulkBusy ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building bulk PDF…</>
                    ) : (
                      <><Package className="w-4 h-4 mr-2" /> Download Bulk PDF</>
                    )}
                  </Button>
                  <Button
                    onClick={() => buildBulkPacket({ emailIt: true })}
                    disabled={bulkBusy || Object.values(bureaus).every(v => !v) || !bulkEmailEnabled || !(bulkEmailTo || '').trim()}
                    className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
                    data-testid="bulk-download-email-btn"
                  >
                    {bulkBusy ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <><Package className="w-4 h-4 mr-2" /> Download & Email</>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════
function PresetRow({ checked, onChange, disabled, label, sub, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        disabled
          ? 'border-zinc-800 bg-zinc-900/40 opacity-60 cursor-not-allowed'
          : checked
            ? 'border-emerald-500/40 bg-emerald-500/5'
            : 'border-zinc-700 bg-zinc-800/40 hover:bg-zinc-800/70'
      }`}
      data-testid={testid}
    >
      <input
        type="checkbox"
        className="mt-0.5 w-4 h-4 accent-emerald-500"
        checked={checked && !disabled}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-white flex items-center gap-2">
          <FileText className={`w-4 h-4 ${checked && !disabled ? 'text-emerald-400' : 'text-zinc-500'}`} />
          {label}
          {checked && !disabled && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Auto</span>
          )}
        </div>
        {sub && <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>}
      </div>
    </label>
  );
}

function VerbatimSlot({ label, file, setFile, testid }) {
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const dropped = Array.from(e.dataTransfer.files || []);
        if (dropped.length) setFile(dropped[0]);
      }}
      className={`rounded-md border-2 border-dashed p-2 transition-colors ${
        file
          ? 'border-emerald-500/50 bg-emerald-500/10'
          : drag
            ? 'border-amber-400 bg-amber-500/10'
            : 'border-zinc-700 bg-zinc-800/40'
      }`}
      data-testid={testid}
    >
      <div className="text-[11px] font-semibold text-white mb-1 flex items-center justify-between gap-2">
        <span className="truncate">{label}</span>
        {file && (
          <button
            onClick={() => setFile(null)}
            className="text-zinc-400 hover:text-red-400 flex-shrink-0"
            aria-label={`remove ${label}`}
            data-testid={`${testid}-clear`}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {file ? (
        <div className="text-[10px] text-emerald-300 truncate" title={file.name}>
          ✓ {file.name}
        </div>
      ) : (
        <label className="text-[10px] text-blue-400 underline cursor-pointer block">
          Drag PDF here or pick file
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ''; }}
            data-testid={`${testid}-input`}
          />
        </label>
      )}
    </div>
  );
}

function ExtraDocsZone({ files, setFiles }) {  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const dropped = Array.from(e.dataTransfer.files || []);
        if (dropped.length) setFiles(prev => [...prev, ...dropped]);
      }}
      className={`rounded-lg border-2 border-dashed p-3 transition-colors ${
        drag ? 'border-blue-400 bg-blue-500/10' : 'border-zinc-700 bg-zinc-800/30'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Package className="w-4 h-4 text-zinc-400" />
        <span className="text-sm font-semibold text-white">Extra documents (optional)</span>
        <span className="text-[11px] text-zinc-500">— additional letters, medical records, police reports…</span>
      </div>
      <label className="text-blue-400 underline cursor-pointer text-[12px]">
        Drag files here or pick from device
        <input
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files || []);
            if (picked.length) setFiles(prev => [...prev, ...picked]);
            e.target.value = '';
          }}
          data-testid="preset-extra-input"
        />
      </label>
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center justify-between bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[12px]">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                <span className="text-zinc-200 truncate">{f.name}</span>
                <span className="text-zinc-500 text-[10px]">{(f.size / 1024).toFixed(0)} KB</span>
              </div>
              <button
                onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                className="text-zinc-400 hover:text-red-400"
                aria-label="remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
